import { i18n } from '@i18n/i18n.service';
import { logger } from '@utils/logger.util';

export type TranslationParams = Record<string, string | number | boolean>;

/**
 * Get translated resource string by key with optional dynamic values
 * Similar to ngx-translate but for React
 *
 * @example
 * // Simple translation - no dynamic values
 * getResource('RPYouWillEarn.Text')
 * // Returns: "You will earn"
 *
 * @example
 * // Dynamic interpolation
 * getResource('VBAsignedtoAdult.Text', { paxName: 'John', paxType: 'Adult' })
 * // If the translation template is "Assigned to {{paxName}} ({{paxType}})"
 * // Returns: "Assigned to John (Adult)"
 *
 * @example
 * // With default fallback
 * getResource('SomeKey.Text', {}, 'Default text')
 * // Returns: "Default text" if key is not found
 *
 * @param key - The translation resource key (e.g., 'RPYouWillEarn.Text')
 * @param params - Optional object with dynamic values for interpolation
 * @param fallback - Optional fallback text if key is not found
 * @returns Translated string with interpolated values
 */
export const getResource = (key: string, params?: TranslationParams, fallback?: string): string => {
  try {
    // Use i18n.t() to get the translation
    // i18n handles interpolation of {{variable}} patterns with the params object
    const translation = i18n.t(key, {
      ...params,
      defaultValue: fallback ?? key,
    });

    return typeof translation === 'string' ? translation : String(translation);
  } catch (error) {
    logger.error(`Failed to translate resource key: ${key}`, error);
    return fallback ?? key;
  }
};

/**
 * Get multiple translated resource strings at once
 * Useful for batch translations
 *
 * @example
 * getResources(['RPYouWillEarn.Text', 'RPContinue.Text', 'RPCancel.Text'])
 * // Returns: { 'RPYouWillEarn.Text': 'You will earn', ... }
 *
 * @param keys - Array of translation resource keys
 * @returns Object with keys and translated values
 */
export const getResources = (keys: string[]): Record<string, string> => {
  const result: Record<string, string> = {};

  keys.forEach((key) => {
    result[key] = getResource(key);
  });

  return result;
};

/**
 * Get translated resource with context-specific parameters
 * Combines key translation with object property values
 *
 * @example
 * // Passenger object: { firstName: 'John', passengerType: 'Adult' }
 * getResourceWithContext('VBAsignedtoAdult.Text', passenger)
 * // If translation uses {{firstName}} and {{passengerType}}
 * // Returns: "Assigned to John (Adult)"
 *
 * @param key - The translation resource key
 * @param contextObject - Object whose properties can be used in interpolation
 * @param fallback - Optional fallback text if key is not found
 * @returns Translated string with interpolated values from context object
 */
export const getResourceWithContext = (
  key: string,
  contextObject: Record<string, unknown>,
  fallback?: string,
): string => {
  const params: TranslationParams = {};

  // Convert context object properties to string params
  Object.entries(contextObject).forEach(([k, v]) => {
    if (v !== null && v !== undefined) {
      params[k] = String(v);
    }
  });

  return getResource(key, params, fallback);
};

/**
 * Check if a translation resource key exists
 * Useful for conditional rendering based on translation availability
 *
 * @example
 * if (hasResourceKey('RPYouWillEarn.Text')) {
 *   // Key exists, safe to use
 * }
 *
 * @param key - The translation resource key to check
 * @returns true if key exists in current language, false otherwise
 */
export const hasResourceKey = (key: string): boolean => {
  const translation = i18n.t(key, { defaultValue: '' });
  // If translation returns the key itself (default behavior), key doesn't exist
  return translation !== key && translation !== '';
};

/**
 * Get the current active language
 * @returns Current language code (e.g., 'en', 'ar')
 */
export const getCurrentLanguage = (): string => {
  return i18n.language;
};

/**
 * Format a resource template with specific parameters
 * Low-level function for custom formatting logic
 *
 * @example
 * formatResourceTemplate('Hello {{name}}, you have {{count}} messages', { name: 'John', count: 5 })
 * // Returns: "Hello John, you have 5 messages"
 *
 * @param template - Template string with {{variable}} placeholders
 * @param params - Object containing variable values
 * @returns Formatted string
 */
export const formatResourceTemplate = (template: string, params?: TranslationParams): string => {
  if (!params || Object.keys(params).length === 0) {
    return template;
  }

  let result = template;

  Object.entries(params).forEach(([key, value]) => {
    const placeholder = `{{${key}}}`;
    result = result.replace(new RegExp(placeholder, 'g'), String(value));
  });

  return result;
};
